import React, { useEffect, useMemo, useState } from 'react'
import styles from './AddDishView.module.css'
import { addDishToBusinessLunch, addDishToSection, getAllMenu, setMenuAvailable } from '../../../api/Menu'
import { getDishImageUrl } from '../utils'
import { IconChevron, IconPlus, IconSearch } from '../icons'

const HIDDEN_KEY = '__hidden'

// API отдаёт dish.section_id = 0 для ВСЕХ блюд независимо от категории,
// поэтому группировать нужно по section_title/section_sort_order, а не по section_id
const groupBySection = menu => {
  const map = new Map()
  menu.forEach(dish => {
    const key = `${dish.section_sort_order}_${dish.section_title}`
    if (!map.has(key)) map.set(key, { id: key, title: dish.section_title, sortOrder: dish.section_sort_order, dishes: [] })
    map.get(key).dishes.push(dish)
  })
  return Array.from(map.values()).sort((a, b) => a.sortOrder - b.sortOrder)
}

// Названия секций в данных не всегда совпадают буква в букву ("Вторые блюда ( бизнес - ланч )"
// vs "Вторые блюда (бизнес-ланч)"), поэтому сравниваем без пунктуации/лишних пробелов
const normalizeTitle = str => (str || '').toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim()

const matchesTerm = (dish, term) => !term || (dish.title_ru || '').toLowerCase().includes(term)

const AddDishView = ({ restId, target, onDone }) => {
  const [allMenu, setAllMenu] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState({ [HIDDEN_KEY]: true })
  const [addingId, setAddingId] = useState(null)
  const [errorId, setErrorId] = useState(null)
  const [showAllCategories, setShowAllCategories] = useState(false)

  useEffect(() => {
    setLoading(true)
    getAllMenu(restId)
      .then(response => setAllMenu(response.data.data || []))
      .finally(() => setLoading(false))
  }, [restId])

  const excludedIds = useMemo(() => new Set(target?.existingIds || []), [target])
  const term = search.trim().toLowerCase()

  // status: 1 — блюдо в меню, 0 — скрыто. Скрытые показываем отдельным блоком.
  const visibleMenu = useMemo(() => allMenu.filter(dish => dish.status !== 0), [allMenu])
  const hiddenMenu = useMemo(() => allMenu.filter(dish => dish.status === 0), [allMenu])

  const filtered = useMemo(
    () => visibleMenu.filter(dish => !excludedIds.has(dish.id) && matchesTerm(dish, term)),
    [visibleMenu, excludedIds, term]
  )

  const allSections = useMemo(() => groupBySection(filtered), [filtered])

  const targetTitle = target?.kind === 'blCategory' ? target.categoryTitle : target?.sectionTitle
  const targetTitleNorm = normalizeTitle(targetTitle)
  const matchingSections = useMemo(
    // разделы пула бизнес-ланча называются "<Категория> (бизнес-ланч)", поэтому сравниваем по префиксу,
    // а не по точному совпадению — иначе для категорий бизнес-ланча совпадений не найдётся никогда
    () => (targetTitleNorm ? allSections.filter(section => normalizeTitle(section.title).startsWith(targetTitleNorm)) : []),
    [allSections, targetTitleNorm]
  )

  const hasMatch = matchingSections.length > 0
  const sections = !search && hasMatch && !showAllCategories ? matchingSections : allSections

  // Скрытые блюда возвращаются в свою прежнюю секцию — для добавления в категорию
  // бизнес-ланча они не подходят, поэтому блок показываем только при добавлении в секцию.
  const hiddenDishes = useMemo(
    () => (target?.kind === 'blCategory' ? [] : hiddenMenu.filter(dish => matchesTerm(dish, term))),
    [hiddenMenu, term, target]
  )

  const toggleSection = id => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }))

  const addDish = async dish => {
    setAddingId(dish.id)
    setErrorId(null)
    try {
      if (target.kind === 'blCategory') {
        await addDishToBusinessLunch(restId, target.blId, { menu_id: dish.id, category: target.categoryId })
      } else {
        await addDishToSection(restId, dish.id, { section_id: target.sectionId })
      }
      onDone()
    } catch (error) {
      console.error('addDish failed', error.response?.status, error.response?.data || error.message)
      setErrorId(dish.id)
    } finally {
      setAddingId(null)
    }
  }

  const restoreDish = async dish => {
    setAddingId(dish.id)
    setErrorId(null)
    try {
      await setMenuAvailable(restId, dish.id, true)
      onDone()
    } catch (error) {
      console.error('restoreDish failed', error.response?.status, error.response?.data || error.message)
      setErrorId(dish.id)
    } finally {
      setAddingId(null)
    }
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.searchRow}>
        <input className={styles.searchInput} placeholder='Введите текст' value={search} onChange={e => setSearch(e.target.value)} />
        <IconSearch className={styles.searchIcon} />
      </div>

      {!loading && !search && hasMatch && (
        <button className={styles.showAllBtn} onClick={() => setShowAllCategories(prev => !prev)}>
          {showAllCategories ? 'Показать только подходящие' : 'Показать все категории'}
        </button>
      )}

      {loading ? (
        <div className={styles.loading}>Загрузка...</div>
      ) : (
        <>
          {sections.map(section => (
            <div className={styles.section} key={`add_section_${section.id}`}>
              <div className={styles.sectionHeader} onClick={() => toggleSection(section.id)}>
                <h3>{section.title}</h3>
                <IconChevron className={collapsed[section.id] ? styles.chevronCollapsed : ''} />
              </div>
              {!collapsed[section.id] &&
                section.dishes.map(dish => (
                  <div className={styles.row} key={dish.id}>
                    <img className={styles.image} src={getDishImageUrl(dish.image)} alt='' />
                    <div className={styles.info}>
                      <span className={styles.title}>{dish.title_ru}</span>
                      <span className={styles.subtitle}>{dish.section_title}</span>
                      {errorId === dish.id && <span className={styles.error}>Не удалось, попробуйте ещё раз</span>}
                    </div>
                    <button className={styles.addBtn} disabled={addingId === dish.id} onClick={() => addDish(dish)}>
                      <IconPlus />
                    </button>
                  </div>
                ))}
            </div>
          ))}

          {hiddenDishes.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionHeader} onClick={() => toggleSection(HIDDEN_KEY)}>
                <h3>Скрытые блюда ({hiddenDishes.length})</h3>
                <IconChevron className={collapsed[HIDDEN_KEY] ? styles.chevronCollapsed : ''} />
              </div>
              {!collapsed[HIDDEN_KEY] &&
                hiddenDishes.map(dish => (
                  <div className={styles.row} key={dish.id}>
                    <img className={styles.image} src={getDishImageUrl(dish.image)} alt='' />
                    <div className={styles.info}>
                      <span className={styles.title}>{dish.title_ru}</span>
                      <span className={styles.subtitle}>{dish.section_title} · скрыто, вернётся в свою секцию</span>
                      {errorId === dish.id && <span className={styles.error}>Не удалось, попробуйте ещё раз</span>}
                    </div>
                    <button className={styles.addBtn} disabled={addingId === dish.id} onClick={() => restoreDish(dish)}>
                      <IconPlus />
                    </button>
                  </div>
                ))}
            </div>
          )}

          {sections.length === 0 && hiddenDishes.length === 0 && <div className={styles.loading}>Ничего не найдено</div>}
        </>
      )}
    </div>
  )
}

export default AddDishView
